/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useRef, useState } from "react";
import { Box, Button, GlobalStyles, Typography, styled } from "@mui/material";
import { useLearnosityPlayer } from "../hooks/useLearnosityPlayer";
import { useLearnositySubmissionExtractor } from "../hooks/useLearnositySubmissionExtractor";
import { AssessmentLayout } from "./AssessmentLayout";
import type { AssessmentViewProps } from "./StandardAssessmentView";

/** Hide the Learnosity built-in Start button on the intro page so we can use our own */
const hideIntroStartButton = (
  <GlobalStyles
    styles={{
      ".lrn-assess .lrn-start-page .start-test-btn": {
        display: "none !important",
      },
    }}
  />
);

const resizeIntroDialog = (
  <GlobalStyles
    styles={{
      ".lrn-dialog-footer": {
        display: "none",
      },
      ".lrn-dialog-header": {
        display: "none",
      },
      ".ladder-assessment-container": {
        position: "absolute",
        top: 20,
        bottom: 100,
        width: "100%",
        marginRight: 0,
        marginLeft: 0,
        left: 0,
        right: 0,
        overflow: "hidden",
      },
    }}
  />
);

const NAV_BAR_HEIGHT = 72;

const NavBarContainer = styled(Box)(({ theme }) => ({
  label: "NavBarContainer",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "16px 0",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  zIndex: 10,
}));

const NavBarSpacer = styled(Box)({
  label: "NavBarSpacer",
  height: `${NAV_BAR_HEIGHT}px`,
});

const NavButton = styled(Button)({
  label: "NavButton",
  fontSize: "1.25em",
  padding: "0.4em 0.9em",
});

interface NavState {
  disabled: boolean;
  label: string;
  onClick: () => void;
}

export const QuizAssessmentView = ({
  ladder,
  learnosityInitOptions,
  sessionId,
  onExit,
  onActivityStarted,
  onActivitySubmitted,
  onSubmissionComplete,
}: AssessmentViewProps) => {
  const onActivityStartedRef = useRef(onActivityStarted);
  onActivityStartedRef.current = onActivityStarted;

  const [navState, setNavState] = useState<NavState>({
    disabled: true,
    label: "Submit",
    onClick: () => {},
  });
  const [ready, setReady] = useState(false);
  const [onIntro, setOnIntro] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);

  // Track last-item status in a ref so closures inside Learnosity handlers stay current
  const isLastItemRef = useRef(false);

  const { submitting, handleSubmitted } = useLearnositySubmissionExtractor({
    ladderType: ladder.ladderType,
    onActivitySubmitted,
    onSubmissionComplete,
  });

  // These refs let Learnosity callbacks access divId + itemsApi without stale closures
  const divIdRef = useRef<string>("");

  const itemsApiRef = useRef<any>(null);

  const handleActivityStarted = useCallback((itemsApi: any) => {
    onActivityStartedRef.current(itemsApi);
  }, []);

  const startFromIntro = useCallback(() => {
    const api = itemsApiRef.current;
    if (!api) return;
    api.assessApp().start();
  }, []);

  // Helper: advance to next item or finish if last
  const goNextOrSubmit = useCallback(() => {
    const api = itemsApiRef.current;
    if (!api) return;
    if (isLastItemRef.current) {
      // Save responses to Learnosity server (fire-and-forget)
      api.submit({ show_submit_confirmation: false, show_submit_ui: false });
      // Directly extract results and show completion screen
      handleSubmitted(api, divIdRef.current);
    } else {
      api.save();
      api.items().next();
    }
  }, [handleSubmitted]);

  const handleReady = useCallback(
    (itemsApiInstance: any) => {
      itemsApiRef.current = itemsApiInstance;

      // Build an index map from item reference → position so we can
      // determine the actual position on every item:load, including resume.
      const items = itemsApiInstance.getItems();
      const itemRefs = Object.keys(items);
      const totalItems = itemRefs.length;
      const refToIndex: Record<string, number> = {};
      itemRefs.forEach((ref, i) => {
        refToIndex[ref] = i;
      });
      setTotalSteps(totalItems);

      // Detect if the Learnosity intro page is showing
      const hasStarted = itemsApiInstance.assessApp?.()?.hasStarted?.();
      if (hasStarted === false) {
        setOnIntro(true);
      }

      // On resume, skip ahead to the first unanswered item
      const firstUnanswered = itemRefs.findIndex(
        (ref) => items[ref].attempt_status !== "fully_attempted",
      );
      if (firstUnanswered > 0) {
        itemsApiInstance.items().goto(firstUnanswered);
      }

      // --- item:load handler ---
      itemsApiInstance.on("item:load", () => {
        setOnIntro(false);
        const currentItem = itemsApiInstance.getCurrentItem();
        const currentRef = currentItem?.reference;
        const currentPosition =
          currentRef != null ? (refToIndex[currentRef] ?? 0) : 0;
        isLastItemRef.current = currentPosition >= totalItems - 1;
        setCurrentStep(currentPosition);

        const isAttempted = currentItem.attempt_status === "fully_attempted";

        if (isAttempted || !currentItem.questions.length) {
          // Already answered (resume) or informational slide — allow advancing
          setNavState({
            disabled: false,
            label: isLastItemRef.current ? "Done" : "Next",
            onClick: goNextOrSubmit,
          });
        } else {
          // Has unanswered questions — disable until answered
          setNavState({
            disabled: true,
            label: "Submit",
            onClick: () => {},
          });
        }
      });

      // --- Per-question handlers ---

      const questions: any[] = Object.values(itemsApiInstance.questions());
      questions.forEach((question) => {
        const validation = question.getQuestion().validation;
        const isUnscored =
          !validation ||
          !Object.prototype.hasOwnProperty.call(validation, "valid_response");

        // Guard flag: once the learner has answered, ignore further "changed" events.
        // This achieves one-try behavior WITHOUT calling question.disable(),
        // which blocks Learnosity's programmatic items().next() navigation.
        let answered = false;

        question.on("changed", () => {
          if (answered) return;

          if (isUnscored) {
            // Unscored: go directly to next/submit
            setNavState({
              disabled: false,
              label: isLastItemRef.current ? "Done" : "Next",
              onClick: () => {
                answered = true;
                goNextOrSubmit();
              },
            });
          } else {
            // Scored: one try — validate, show feedback, then advance
            setNavState({
              disabled: false,
              label: "Submit",
              onClick: () => {
                answered = true;
                question.validate({
                  showCorrectAnswers: false,
                  showDistractorRationale: true,
                });
              },
            });
          }
        });

        question.on("validated", () => {
          // Lock the question so the learner cannot change their answer
          question.disable();
          // One try: always advance after seeing feedback
          setNavState({
            disabled: false,
            label: isLastItemRef.current ? "Done" : "Next",
            onClick: () => {
              // Re-enable before navigating — disable() blocks items().next()
              question.enable();
              goNextOrSubmit();
            },
          });
        });
      });

      setReady(true);
    },
    [goNextOrSubmit],
  );

  const {
    divId,
    loading: activityLoading,
    itemsApi,
    error: hadError,
  } = useLearnosityPlayer({
    learnosityInitOptions,
    sessionId,
    events: {
      "test:start": handleActivityStarted,
      "test:resume": handleActivityStarted,
    },
    onReady: handleReady,
  });

  // Keep divIdRef in sync for the submit handler
  divIdRef.current = divId;

  return (
    <>
      {onIntro && resizeIntroDialog}
      {hideIntroStartButton}
      <AssessmentLayout
        ladder={ladder}
        divId={divId}
        loading={activityLoading}
        submitting={submitting}
        error={hadError}
        onExit={onExit}
        itemsApi={itemsApi}
        header={
          ready && totalSteps > 0 && !onIntro ? (
            <Typography
              variant="h5"
              sx={{ textAlign: "center", fontWeight: 700, mb: 1 }}
            >
              Question {currentStep + 1} out of {totalSteps}
            </Typography>
          ) : undefined
        }
      >
        {ready && (
          <>
            <NavBarSpacer />
            <NavBarContainer>
              <NavButton
                variant="contained"
                color="primary"
                disabled={onIntro ? false : navState.disabled}
                onClick={onIntro ? startFromIntro : navState.onClick}
              >
                {onIntro ? "Next" : navState.label}
              </NavButton>
            </NavBarContainer>
          </>
        )}
      </AssessmentLayout>
    </>
  );
};
